import type { Dispatch, RefObject } from 'react';
import { createContext, useContext } from 'react';

import type { FocusOverride, ParsedToken, QueryBuilderAction } from '../../types/QueryBuilder';

export type ActivePopover = {
  tokenKey: string;
  part: 'key' | 'op' | 'value' | 'type' | 'operator';
  /**
   * When true, the popover opens with the first concrete option pre-highlighted so the
   * user can press Enter to commit it without first navigating with arrow keys.
   * Set by FreeTextToken when auto-creating a lozenge from a custom field name —
   * the user has already shown intent, so the very next keystroke should commit
   * (exists / does not exist are skipped so Enter lands on "is").
   */
  autoFocusFirst?: boolean;
  draft?: string;
} | null;

export interface FieldOwner {
  focus: () => void;
}

export interface QueryBuilderContextValue {
  query: string;
  committedQuery: string;
  parsedQuery: ParsedToken[];
  parsedCommittedQuery: ParsedToken[];
  /** Also available for mid-action use inside reducers/hooks */
  parseQuery: (query: string) => ParsedToken[];
  dispatch: Dispatch<QueryBuilderAction>;
  focusOverride: FocusOverride | null;
  wrapperRef: RefObject<HTMLDivElement | null>;
  activePopover: ActivePopover;
  setActivePopover: (popover: ActivePopover) => void;
  activePopoverRef: RefObject<ActivePopover>;
  /** Open value-editor field; inline editors never set `activePopover`. */
  activeValueField: string | undefined;
  setActiveValueField: (field: string | undefined) => void;
  /** Typed text in the open value editor; empty until the user edits (consumers search on this). */
  activeValueSearch: string | undefined;
  setActiveValueSearch: (search: string | undefined) => void;
  registerFieldOwner: (fieldKey: string, owner: FieldOwner) => () => void;
  getFieldOwner: (fieldKey: string) => FieldOwner | undefined;
  /** Serialize the current Lezer query string for Cmd/Ctrl+C / Cmd/Ctrl+X. */
  formatClipboard: (query: string) => string;
  /** Normalize pasted text into a Lezer query before parseQuery. */
  parseClipboard: (text: string) => string;
  suppressNextPopoverCloseCommit: () => void;
}

const QueryBuilderContext = createContext<QueryBuilderContextValue | null>(null);

export function useQueryBuilderContext(): QueryBuilderContextValue {
  const ctx = useContext(QueryBuilderContext);
  if (!ctx) {
    throw new Error('useQueryBuilderContext must be used within a QueryBuilder');
  }
  return ctx;
}

export { QueryBuilderContext };
